/**
 * Demo Experience Context
 *
 * Wraps the entire app so any page can tell whether the current user is
 * exploring Meybra through the public /demo entry point. The visitor's
 * demo session id (persisted in localStorage) drives:
 *   - The persistent "Demo Environment" banner
 *   - The panel switcher (Club Dashboard / Team Coach / Team Manager /
 *     Athlete-Parent) available at the top of the app
 *   - Silent page-view tracking for the demo analytics dashboard
 *
 * Auth is intentionally reused — the demo backend mints a normal Meybra
 * JWT (that impersonates a real academy account) so every existing route
 * works unchanged.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'meybra_demo_session_id';

/** Seed the exact same localStorage keys unified-login would set for the
 *  target role, so every existing layout guard works unchanged during a
 *  demo session. Called after /demo/start and every /demo/switch-panel. */
const seedRoleStorage = (token, user) => {
  if (!token || !user) return;
  localStorage.setItem('meybra_token', token);
  const role = (user.role || '').toLowerCase();
  if (role === 'club_admin') {
    localStorage.setItem('club_token', token);
    localStorage.setItem('club_user', JSON.stringify(user));
    localStorage.setItem('club_admin', JSON.stringify(user));
    if (user.club_id) {
      localStorage.setItem('club_info', JSON.stringify({ id: user.club_id }));
    }
  } else if (role === 'team_coach') {
    localStorage.setItem('team_token', token);
    localStorage.setItem('team_coach', JSON.stringify(user));
    localStorage.setItem('team_user', JSON.stringify(user));
    if (user.teams) localStorage.setItem('team_teams', JSON.stringify(user.teams));
  } else if (role === 'team_manager') {
    localStorage.setItem('team_token', token);
    localStorage.setItem('team_manager', JSON.stringify(user));
    localStorage.setItem('team_user', JSON.stringify(user));
    if (user.teams) localStorage.setItem('team_teams', JSON.stringify(user.teams));
  } else if (role === 'platform_admin') {
    localStorage.setItem('platform_admin_token', token);
    localStorage.setItem('platform_admin_user', JSON.stringify(user));
  }
  // coach / parent / athlete rely on meybra_token + /api/auth/me — no
  // additional keys needed.
};

/** Wipe any role-scoped storage we may have seeded so exiting the demo
 *  leaves no ghost sessions behind. */
const wipeRoleStorage = () => {
  ['meybra_token','club_token','club_user','club_admin','club_info',
   'team_token','team_coach','team_manager','team_user','team_teams',
   'platform_admin_token','platform_admin_user']
    .forEach((k) => { try { localStorage.removeItem(k); } catch { /* noop */ } });
};

const DemoContext = createContext(null);

export const useDemo = () => useContext(DemoContext) || { session: null, isDemo: false };

export const DemoProvider = ({ children }) => {
  const [sessionId, setSessionIdState] = useState(() => localStorage.getItem(STORAGE_KEY));
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(!!sessionId);

  const setSessionId = useCallback((id) => {
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    setSessionIdState(id);
  }, []);

  const refresh = useCallback(async (id) => {
    const target = id || sessionId;
    if (!target) { setSession(null); return; }
    try {
      const res = await axios.get(`${API}/demo/session/${target}`);
      setSession(res.data);
    } catch {
      // Session expired or missing — clear silently so the visitor lands
      // back on the public /demo entry the next time they navigate.
      setSession(null);
      setSessionId(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId, setSessionId]);

  useEffect(() => {
    if (sessionId) { refresh(sessionId); } else { setLoading(false); }
  }, [sessionId, refresh]);

  const track = useCallback((page, extras = {}) => {
    if (!sessionId) return;
    // Best-effort — never block navigation on analytics.
    axios.post(`${API}/demo/track?session_id=${sessionId}`, {
      page,
      event: extras.event || 'view',
      seconds: extras.seconds,
    }).catch(() => {});
  }, [sessionId]);

  const switchPanel = useCallback(async (panel) => {
    if (!sessionId) return null;
    const res = await axios.post(`${API}/demo/switch-panel?session_id=${sessionId}`, { panel });
    // Seed the exact localStorage keys the target role's layout guard expects
    // so the entire UI hydrates as if the visitor freshly logged in as that
    // account. Wipe first so no stale keys from the previous panel remain.
    wipeRoleStorage();
    seedRoleStorage(res.data.token, res.data.user);
    await refresh(sessionId);
    return res.data;
  }, [sessionId, refresh]);

  const endDemo = useCallback(async ({ message, kind = 'exit', contactOk = false } = {}) => {
    if (!sessionId) return;
    try {
      await axios.post(`${API}/demo/end?session_id=${sessionId}`, { message, kind, contact_ok: contactOk });
    } catch { /* noop */ }
    wipeRoleStorage();
    setSessionId(null);
    setSession(null);
  }, [sessionId, setSessionId]);

  const value = useMemo(() => ({
    session,
    sessionId,
    isDemo: !!sessionId && !!session,
    loading,
    setSessionId,
    refresh,
    track,
    switchPanel,
    endDemo,
    seedRoleStorage,
    wipeRoleStorage,
  }), [session, sessionId, loading, setSessionId, refresh, track, switchPanel, endDemo]);

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
};

export default DemoContext;
